import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import AuthLoginView from './auth-login.component.html';

@Component({
    selector: 'auth-login',
    template: AuthLoginView
})
export class AuthLoginComponent implements OnInit {

    processes: any[] = [];
    services: any[] = [];
    step: any = null;

    showProcessSelector = false;
    showNewLogin = false;
    showCompanyData = false;
    companyLogoUrl = '';
    companyName = '';

    loginData = { username: '', password: '', authenticator_code: '', yubico_otp: '' };
    rememberMe = false;
    passwordVisible = false;
    activeTab: 'password' | 'webauthn' | 'external' | 'authenticator' | 'yubicootp' = 'password';

    showForgotPassword = false;
    forgotPasswordEmail = '';
    forgotPasswordSent = false;

    webauthnRegistrationToken: string | null = null;
    webauthnRegistrationInProgress = false;

    // Propiedades OAuth callback
    isOauthCallback = false;
    oauthCallbackState: 'loading' | 'error' = 'loading';
    oauthProviderName = '';
    oauthErrorMessage = '';
    oauthErrorDetailsVisible = false;

    constructor(
        @Inject('LoginService') private loginService: any,
        @Inject('$rootScope') private $rootScope: any,
        @Inject('$state') private $state: any,
        @Inject(HttpClient) private http: HttpClient,
        @Inject(NgZone) private ngZone: NgZone
    ) {}

    ngOnInit(): void {
        const code = this.getCodeFromUrl();
        if (code) {
            const authServiceRaw = localStorage.getItem('authService');
            if (!authServiceRaw) {
                this.$state.go('authinit');
                return;
            }
            let service: any;
            try {
                service = JSON.parse(authServiceRaw);
            } catch {
                this.$state.go('authinit');
                return;
            }
            this.isOauthCallback = true;
            this.oauthProviderName = service.name || '';
            this.exchangeOauthCode(code, service);
            return;
        }

        // Inactivity logout message
        if (this.$rootScope.inactivityLogout) {
            this.$rootScope.loginStepErrorMessage = 'Sesión caducada por inactividad.';
            this.$rootScope.inactivityLogout = null;
        }

        // Preload remembered username
        const remembered = this.loginService.wantToBeRemembered();
        if (remembered === 'true') {
            this.loginData.username = this.loginService.get('user') || '';
            this.rememberMe = true;
        }

        // Company info
        this.http.get<number>('/api/Companies/Count').subscribe({
            next: (count) => {
                if (count !== 0) {
                    this.showCompanyData = true;
                    this.companyLogoUrl = `/api/Companies/${count}/logo`;
                    this.http.get(`/api/Companies/${count}/name`, { responseType: 'text' }).subscribe({
                        next: (name) => { this.companyName = name; }
                    });
                }
                this.showNewLogin = true;
            }
        });

        // OAuth services
        this.http.get<any[]>('/api/Auth/Services').subscribe({
            next: (services) => {
                this.services = services
                    .filter(s => s.openid_configuration != null && s.webapp_enabled === true)
                    .map(s => ({ ...s, imageUrl: `/api/Files/${s.imageUid}/download` }));
                this.tryAutoLoginExternal();
            }
        });

        // Auth processes
        this.http.get<any[]>('/api/AuthenticationProcesses/Enabled').subscribe({
            next: (processes) => {
                this.processes = processes;
                if (processes.length === 0) {
                    this.$rootScope.loginStepErrorMessage = 'No hay procesos de autenticación configurados.';
                    this.showNewLogin = true;
                    return;
                }
                if (processes.length === 1) {
                    this.selectProcess(processes[0]);
                } else {
                    this.loginService.nextStep().then((step: any) => {
                        this.ngZone.run(() => {
                            this.showProcessSelector = !step;
                            if (step) { this.step = step; this.setActiveTab(); }
                        });
                    });
                }
            }
        });
    }

    selectProcess(process: any): void {
        this.showProcessSelector = false;
        this.loginService.clearLoginStepError();
        this.loginService.nextStep(process.id).then((step: any) => {
            this.ngZone.run(() => {
                this.step = step;
                this.setActiveTab();
                this.tryAutoLoginExternal();
            });
        });
    }

    resetProcess(): void {
        this.loginService.reset();
    }

    get activeMethods(): string[] {
        if (!this.step) return [];
        const methods: string[] = [];
        if (this.step.UserPassword) methods.push('password');
        if (this.step.WebAuthn) methods.push('webauthn');
        if (this.step.OAuth) methods.push('external');
        if (this.step.Authenticator) methods.push('authenticator');
        if (this.step.YubicoOTP) methods.push('yubicootp');
        return methods;
    }

    get hasTabs(): boolean {
        return this.activeMethods.length > 1;
    }

    get showBackLink(): boolean {
        const validSteps = this.$rootScope.step?.ValidSteps || this.$rootScope.step?.validSteps || 0;
        return validSteps > 0 || this.processes.length > 1;
    }

    get loginStepErrorMessage(): string | null {
        return this.$rootScope.loginStepErrorMessage || null;
    }

    setTab(tab: 'password' | 'webauthn' | 'external' | 'authenticator' | 'yubicootp'): void {
        this.activeTab = tab;
        localStorage.setItem('stela_auth_preferred_tab', tab);
        this.loginService.clearLoginStepError();
        this.showForgotPassword = false;
    }

    // Contraseña tab
    checkUserPassword(): void {
        if ((this.step as any)?.PasswordFallbackAllowed) {
            // Paso WebAuthn con fallback a contraseña habilitado
            (this.loginService as any).validateWebauthnFallback({ ...this.loginData, remember: this.rememberMe })
                .then((result: any) => {
                    this.ngZone.run(() => {
                        if (result?.success) {
                            this.webauthnRegistrationToken = result.registrationToken || null;
                            if (!this.webauthnRegistrationToken) {
                                this.loginService.nextStep().then((step: any) => {
                                    this.ngZone.run(() => {
                                        if (step) { this.step = step; this.setActiveTab(); }
                                    });
                                });
                            }
                            // Si hay token, el template muestra el prompt de registro
                        }
                    });
                });
        } else {
            this.loginService.validateUser({ ...this.loginData, remember: this.rememberMe }).then((result: any) => {
                this.ngZone.run(() => {
                    if (result && (result.UserPassword !== undefined || result.WebAuthn !== undefined
                        || result.OAuth !== undefined || result.Authenticator !== undefined
                        || result.YubicoOTP !== undefined)) {
                        this.step = result;
                        this.setActiveTab();
                    }
                });
            });
        }
    }

    confirmWebauthnRegistration(): void {
        if (!this.webauthnRegistrationToken) return;
        this.webauthnRegistrationInProgress = true;
        (this.loginService as any).registerWebauthnCredential(this.webauthnRegistrationToken)
            .then(() => {
                this.ngZone.run(() => {
                    this.webauthnRegistrationToken = null;
                    this.webauthnRegistrationInProgress = false;
                    this.loginService.nextStep().then((step: any) => {
                        this.ngZone.run(() => {
                            if (step) { this.step = step; this.setActiveTab(); }
                        });
                    });
                });
            })
            .catch(() => {
                this.ngZone.run(() => {
                    this.webauthnRegistrationInProgress = false;
                    this.webauthnRegistrationToken = null;
                    this.loginService.setLoginStepError('No se pudo registrar la passkey. Puedes añadirla más adelante desde tu perfil.');
                    this.loginService.nextStep().then((step: any) => {
                        this.ngZone.run(() => {
                            if (step) { this.step = step; this.setActiveTab(); }
                        });
                    });
                });
            });
    }

    skipWebauthnRegistration(): void {
        this.webauthnRegistrationToken = null;
        this.loginService.nextStep().then((step: any) => {
            this.ngZone.run(() => {
                if (step) { this.step = step; this.setActiveTab(); }
            });
        });
    }

    onKeyPressPassword(event: KeyboardEvent): void {
        if (event.key === 'Enter') this.checkUserPassword();
    }

    sendForgotPassword(): void {
        this.http.post('/api/Auth/ForgotPassword', {
            email: this.forgotPasswordEmail,
            baseUrl: window.location.origin
        }).subscribe({
            next: () => { this.forgotPasswordSent = true; },
            error: () => { this.forgotPasswordSent = true; }
        });
    }

    // WebAuthn tab
    checkWebauthn(): void {
        this.loginService.checkWebauthn();
    }

    // External tab
    loginExternal(service: any): void {
        this.loginService.loginExternal(service);
    }

    // Authenticator tab
    handleOtpChange(code: string | null): void {
        this.loginData.authenticator_code = code ?? '';
        if ((code ?? '').length === 6) this.verifyCode();
    }

    verifyCode(): void {
        this.loginService.validateAuthenticator(this.loginData).then((result: any) => {
            this.ngZone.run(() => {
                if (result && (result.UserPassword !== undefined || result.WebAuthn !== undefined
                    || result.OAuth !== undefined || result.Authenticator !== undefined
                    || result.YubicoOTP !== undefined)) {
                    this.step = result;
                    this.setActiveTab();
                }
            });
        });
    }

    // YubicoOTP tab
    onYubicoInput(): void {
        if (this.loginData.yubico_otp.length === 44) this.verifyYubicoOTP();
    }

    verifyYubicoOTP(): void {
        this.loginService.validateYubicoOTP(this.loginData).then((result: any) => {
            this.ngZone.run(() => {
                if (result && (result.UserPassword !== undefined || result.WebAuthn !== undefined
                    || result.OAuth !== undefined || result.Authenticator !== undefined
                    || result.YubicoOTP !== undefined)) {
                    this.step = result;
                    this.setActiveTab();
                }
            });
        });
    }

    private setActiveTab(): void {
        if (!this.step) return;
        const saved = localStorage.getItem('stela_auth_preferred_tab') as typeof this.activeTab | null;
        if (saved && this.activeMethods.includes(saved)) {
            this.activeTab = saved;
            return;
        }
        // WebAuthn con credenciales en servidor: preferir passkey
        if (this.step.WebAuthn && (this.step as any).HasWebauthnCredentials !== false) this.activeTab = 'webauthn';
        else if (this.step.UserPassword) this.activeTab = 'password';
        else if (this.step.WebAuthn) this.activeTab = 'webauthn';
        else if (this.step.OAuth) this.activeTab = 'external';
        else if (this.step.Authenticator) this.activeTab = 'authenticator';
        else if (this.step.YubicoOTP) this.activeTab = 'yubicootp';
    }

    private tryAutoLoginExternal(): void {
        if (this.services.length === 1 && this.step
            && !this.step.UserPassword && !this.step.Authenticator
            && !this.step.YubicoOTP && !this.step.WebAuthn && this.step.OAuth) {
            this.loginExternal(this.services[0]);
        }
    }

    protected getCodeFromUrl(): string | null {
        return new URLSearchParams(window.location.search).get('code');
    }

    goBackToLogin(): void {
        this.$state.go('authinit');
    }

    toggleOauthErrorDetails(): void {
        this.oauthErrorDetailsVisible = !this.oauthErrorDetailsVisible;
    }

    private exchangeOauthCode(code: string, service: any): void {
        localStorage.removeItem('authService');

        let openid: any;
        try {
            openid = JSON.parse(service.openid_configuration);
        } catch {
            this.ngZone.run(() => {
                this.oauthCallbackState = 'error';
                this.oauthErrorMessage = 'Configuración del proveedor inválida.';
            });
            return;
        }

        let payload: any;
        let url: string;
        let headers: { [key: string]: string };

        const basePayload = {
            client_id: service.client_id,
            code: code,
            grant_type: 'authorization_code',
            redirect_uri: service.callback_url,
            code_verifier: service.codeVerifier,
        };

        if (service.client_secret != null) {
            (basePayload as any).client_secret = service.client_secret;
        }

        if (openid.token_endpoint.indexOf('google') > -1) {
            // Google requires the backend mobile proxy — matches original LoginCtrl behavior
            url = '/api/mobile/auth/oauth/token';
            payload = JSON.stringify({ ...basePayload, uri: openid.token_endpoint, code: code });
            headers = { 'Content-Type': 'application/json' };
        } else if (service.proxy_token_request === true) {
            // Generic proxy: same as Google but different proxy URL
            url = '/api/auth/oauth/token';
            payload = JSON.stringify({ ...basePayload, uri: openid.token_endpoint, code: code });
            headers = { 'Content-Type': 'application/json' };
        } else {
            // Direct to token endpoint: form-encoded
            url = openid.token_endpoint;
            const params = new URLSearchParams();
            Object.entries(basePayload).forEach(([k, v]) => params.set(k, v as string));
            payload = params.toString();
            headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        }

        this.http.post<any>(url, payload, { headers }).subscribe({
            next: (data) => this.onOauthTokenSuccess(data),
            error: (err) => {
                this.ngZone.run(() => {
                    this.oauthCallbackState = 'error';
                    this.oauthErrorMessage = err?.error?.error_description || err?.error?.error || err?.message || 'Error desconocido.';
                });
            }
        });
    }

    private onOauthTokenSuccess(data: any): void {
        if (typeof data === 'string') {
            try { data = JSON.parse(data); } catch { /* use as-is */ }
        }

        let accessToken = data.access_token;
        // If not a JWT (3 parts), fall back to id_token
        if (!accessToken || accessToken.split('.').length !== 3) {
            accessToken = data.id_token;
        }

        this.loginService.set('accessToken', accessToken);
        this.loginService.set('refreshToken', data.refresh_token);
        this.loginService.set('expires_in', data.expires_in);
        if (data.expires_in != null) {
            this.loginService.set('accessTokenValidTo', new Date(data.expires_in * 1000 + Date.now()).toISOString());
        }

        this.http.get<any>('/api/Auth/UserInfo').subscribe({
            next: (userInfo) => {
                this.ngZone.run(() => {
                    this.loginService.set('userId', userInfo.user_id);
                    this.loginService.set('userIcon', userInfo.user_icon);
                    this.loginService.set('companyId', userInfo.company_id);
                    this.loginService.set('rolesPermissions', userInfo.roles_permissions);
                    this.$state.go('dashboard.list');
                });
            },
            error: (err) => {
                this.ngZone.run(() => {
                    this.oauthCallbackState = 'error';
                    this.oauthErrorMessage = 'No se pudo obtener la información del usuario.';
                });
            }
        });
    }
}
